import {
    exifForReencoded,
    exifOrientation,
    exifSegment,
    jpegDimensions,
    withExif
} from './jpegMetadata';
import { isPortableImage, mayHaveAlpha, sniffImageType } from './imageFormat';

/**
 * Downscale + re-encode a captured image before it enters the upload queue.
 * Field photos from phone cameras are often 4000+px / several MB; storing and
 * uploading that over cellular is wasteful for documentation imagery. We cap the
 * longest edge and re-encode as JPEG. EXIF orientation is normalized by decoding
 * through createImageBitmap({ imageOrientation: 'from-image' }); the rest of the
 * original's EXIF — time, GPS, camera — is carried over (see jpegMetadata).
 *
 * A JPEG already within the cap is NOT re-encoded: every re-encode loses a
 * little, the stored photo is the archive, and for a 12 MP phone photo it saved
 * a few percent at the cost of a second of the phone's time.
 */

export interface CompressedImage {
    blob: Blob;
    mimeType: string;
}

export interface CompressOptions {
    /**
     * Keep a fitting JPEG even when its EXIF says to turn it. Right for an
     * upload — the browser, the sketch editor and the server's previews all
     * honour the tag. Not for an image going somewhere that may not (a PDF):
     * there it is re-encoded upright.
     */
    keepTurned?: boolean;
}

/**
 * 4032 is not a round number, it is THE number: a 12 MP phone camera — what
 * nearly every field photo comes from — shoots 4032x3024, so at this cap such a
 * photo is not resized at all, only re-encoded. A cap of 4000 would shrink it by
 * 0.8%: all of the resampling, none of the benefit. A 48 MP phone shooting
 * 8064x6048 halves exactly, which is the cleanest downscale there is.
 *
 * Up from 2560 / 0.90 (itself up from 2048 / 0.82). Each step was the same
 * finding: the stored photo IS the archive — there is no original kept anywhere
 * behind it — and it was visibly softer than what the camera took. Roughly
 * 1.2 MB -> 2.5-3 MB per photo, which a phone still uploads over cellular and
 * the 30 MB server cap clears many times over.
 */
const DEFAULT_MAX_DIMENSION = 4032;
const DEFAULT_QUALITY = 0.9;

/**
 * The JPEG as it is, when nothing would be gained by re-encoding it: within the
 * cap, and either upright or allowed to stay turned. Read from its header, so
 * a photo that fits costs no decoding at all.
 */
function fitsAsIs(
    bytes: Uint8Array,
    maxDimension: number,
    keepTurned: boolean
): boolean {
    const size = jpegDimensions(bytes);
    if (!size || Math.max(size.width, size.height) > maxDimension) {
        return false;
    }
    return keepTurned || exifOrientation(exifSegment(bytes)) === 1;
}

/** Long edge of the copy the transparency check looks at. */
const ALPHA_SAMPLE_EDGE = 512;

/**
 * Whether any of the picture is see-through, judged on a small copy: a scan of
 * every pixel of a large image would cost the phone more than the answer is
 * worth. A spot too small to survive the copy is missed, and then lands on the
 * white ground a JPEG gets (see below) — white, not black.
 */
function hasTransparency(bitmap: ImageBitmap): boolean {
    const scale = Math.min(
        1,
        ALPHA_SAMPLE_EDGE / Math.max(bitmap.width, bitmap.height)
    );
    const width = Math.max(1, Math.round(bitmap.width * scale));
    const height = Math.max(1, Math.round(bitmap.height * scale));
    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;
    const context = canvas.getContext('2d', { willReadFrequently: true });
    if (!context) {
        return false;
    }
    context.drawImage(bitmap, 0, 0, width, height);
    const { data } = context.getImageData(0, 0, width, height);
    for (let i = 3; i < data.length; i += 4) {
        if (data[i] < 255) {
            return true;
        }
    }
    return false;
}

function encode(
    canvas: HTMLCanvasElement,
    type: string,
    quality: number
): Promise<Blob | null> {
    return new Promise((resolve) => {
        canvas.toBlob((result) => resolve(result), type, quality);
    });
}

/**
 * Enough of the file for everything read before decoding: the format, the
 * frame size, the EXIF segment (64 KB at most). Not the whole file — a PDF
 * export runs several photos at once, and a full copy of each was megabytes
 * held for a few hundred bytes of header. Should the frame lie further in,
 * the size reads as unknown and the photo is simply re-encoded.
 */
const HEADER_BYTES = 256 * 1024;

async function readHeader(input: Blob): Promise<Uint8Array> {
    try {
        return new Uint8Array(await input.slice(0, HEADER_BYTES).arrayBuffer());
    } catch {
        // Unreadable (a cloud file gone missing): decoding fails the same way.
        return new Uint8Array();
    }
}

export async function compressImage(
    input: Blob,
    maxDimension = DEFAULT_MAX_DIMENSION,
    quality = DEFAULT_QUALITY,
    { keepTurned = false }: CompressOptions = {}
): Promise<CompressedImage> {
    const bytes = await readHeader(input);
    // What the file IS, not what its name says (see imageFormat).
    const type =
        sniffImageType(bytes) ?? (input.type || 'application/octet-stream');
    const asIs = (): CompressedImage => ({
        blob: input.type === type ? input : new Blob([input], { type }),
        mimeType: type
    });

    // Only attempt to transcode raster images; anything else passes through.
    if (!type.startsWith('image/')) {
        return asIs();
    }

    try {
        const original = type === 'image/jpeg' ? bytes : null;
        if (original && fitsAsIs(original, maxDimension, keepTurned)) {
            return asIs();
        }

        const bitmap = await createImageBitmap(input, {
            imageOrientation: 'from-image'
        });
        const scale = Math.min(
            1,
            maxDimension / Math.max(bitmap.width, bitmap.height)
        );
        const width = Math.max(1, Math.round(bitmap.width * scale));
        const height = Math.max(1, Math.round(bitmap.height * scale));
        /**
         * A see-through picture — a plan, a cut-out, a graphic — stays one: a
         * JPEG has no transparency, and the canvas fills it in BLACK. It goes
         * out as PNG, the one format with transparency the PDF can embed.
         */
        const transparent = mayHaveAlpha(type) && hasTransparency(bitmap);
        if (transparent && type === 'image/png' && scale === 1) {
            bitmap.close();
            return asIs();
        }

        const canvas = document.createElement('canvas');
        canvas.width = width;
        canvas.height = height;
        const ctx = canvas.getContext('2d');
        if (!ctx) {
            bitmap.close();
            return asIs();
        }
        if (!transparent) {
            // White, not the canvas's black, under anything the check missed.
            ctx.fillStyle = '#ffffff';
            ctx.fillRect(0, 0, width, height);
        }
        // The default 'low' DROPS pixels on a 2x downscale instead of
        // averaging them — a 48 MP upload, and every photo into a PDF.
        ctx.imageSmoothingQuality = 'high';
        ctx.drawImage(bitmap, 0, 0, width, height);
        bitmap.close();

        const outputType = transparent ? 'image/png' : 'image/jpeg';
        const encoded = await encode(canvas, outputType, quality);
        if (!encoded) {
            return asIs();
        }
        const exif = original ? exifSegment(original) : null;
        const blob = exif
            ? new Blob(
                  [
                      withExif(
                          new Uint8Array(await encoded.arrayBuffer()),
                          exifForReencoded(exif, { width, height })
                      )
                  ],
                  { type: 'image/jpeg' }
              )
            : encoded;
        // If re-encoding somehow produced a larger file (e.g. already-small
        // image), keep the original — unless it is turned and must not be, or
        // in a format the PDF cannot embed.
        const mustTurn = !keepTurned && exifOrientation(exif) !== 1;
        if (
            blob.size >= input.size &&
            scale === 1 &&
            !mustTurn &&
            isPortableImage(type)
        ) {
            return asIs();
        }
        return { blob, mimeType: outputType };
    } catch {
        // Decode/encode failed (unsupported format, memory) — upload as-is.
        return asIs();
    }
}
